import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CategoryPicker from "@/components/categories/CategoryPicker";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * The trades a FixyBee works in, editable after sign-up (2026-10-28).
 *
 * Sign-up asks for one main trade; this is where they add the rest. Saved
 * through `PUT /users/me/services`, which keeps at least one and syncs the push
 * tags after its commit — so the button stays off with nothing picked rather
 * than letting the server refuse it.
 */
export default function ServicesCard() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [value, setValue] = useState(user?.profile?.service_categories || []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/users/me/services", {
        service_categories: value,
        // Specialties of a trade that is still picked are kept; the rest go
        // with the trade.
        service_subcategories: (user?.profile?.service_subcategories || [])
          .filter((s) => value.some((c) => s.startsWith(`sub_${c.replace(/^cat_/, "")}_`))),
      });
      await refresh();
      setMsg({ ok: true, text: t("signupX.servicesSaved") });
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, t("signupX.servicesFail")) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card-base p-6 mt-6" data-testid="services-card">
      <h2 className="font-display text-xl font-bold text-worker">{t("signupX.servicesTitle")}</h2>
      <p className="mt-1 mb-5 text-smoke text-sm">{t("signupX.servicesHint")}</p>
      <CategoryPicker value={value} onChange={setValue} testid="settings-category" />
      {msg && (
        <p className={`mt-4 text-sm ${msg.ok ? "text-success" : "text-danger"}`} role="status" data-testid="services-msg">
          {msg.text}
        </p>
      )}
      <button type="button" onClick={save} disabled={saving || value.length === 0}
              className="fx-btn fx-btn--secondary w-full sm:w-auto mt-5" data-testid="services-save">
        {saving ? t("signupX.servicesSaving") : t("signupX.servicesSave")}
      </button>
    </section>
  );
}
